import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toggleMultiflowVozSoftphone } from "../../utils/voiceSoftphone";
import { unlockVoiceAudio } from "../../utils/voiceRingtone";
import { maybeRequestVoiceNotifyOnGesture } from "../../utils/voiceDesktopNotify";

const BUTTON_POS_KEY = "multiflow_voz_fab_position";
const FAB_ID = "multiflow_voz_fab";
const DRAG_THRESHOLD_PX = 6;

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed",
    zIndex: 9998,
    cursor: "grab",
    // Impede scroll da página enquanto arrasta no mobile
    touchAction: "none",
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
    "&:active": {
      cursor: "grabbing",
    },
  },
  fabPositionDefault: {
    bottom: 85,
    right: 25,
  },
  button: {
    width: 45,
    height: 45,
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#00339E",
    color: "#fff",
    boxShadow: theme.shadows[4],
    cursor: "inherit",
    touchAction: "none",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: theme.shadows[8],
    },
    "&:focus": {
      outline: "none",
    },
  },
}));

function clampPos(x, y, el) {
  const w = el?.offsetWidth || 45;
  const h = el?.offsetHeight || 45;
  const maxX = Math.max(0, window.innerWidth - w);
  const maxY = Math.max(0, window.innerHeight - h);
  return {
    x: Math.max(0, Math.min(x, maxX)),
    y: Math.max(0, Math.min(y, maxY)),
  };
}

/**
 * FAB Multiflow Voz — abre/fecha o softphone embutido.
 * Arrastável no desktop e no mobile; posição persiste no localStorage.
 */
const MultiflowVozFab = ({ token, onOpen, channelsCount = 1, channelName }) => {
  const classes = useStyles();
  const [buttonPosition, setButtonPosition] = useState(null);
  const positionRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);
  const pointerStartRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(BUTTON_POS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          parsed &&
          typeof parsed.x === "number" &&
          typeof parsed.y === "number"
        ) {
          setButtonPosition(parsed);
          positionRef.current = parsed;
        }
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const persistPosition = useCallback(() => {
    const pos = positionRef.current;
    if (!pos) return;
    try {
      localStorage.setItem(BUTTON_POS_KEY, JSON.stringify(pos));
    } catch (_) {
      // ignore
    }
  }, []);

  const beginDrag = useCallback((clientX, clientY) => {
    const target = document.getElementById(FAB_ID);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    if (!positionRef.current) {
      const initial = { x: rect.left, y: rect.top };
      positionRef.current = initial;
      setButtonPosition(initial);
    }
    isDraggingRef.current = true;
    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    pointerStartRef.current = { x: clientX, y: clientY };
    didDragRef.current = false;
  }, []);

  const moveDrag = useCallback((clientX, clientY) => {
    if (!isDraggingRef.current) return;
    const target = document.getElementById(FAB_ID);
    if (!target) return;

    const dx = clientX - pointerStartRef.current.x;
    const dy = clientY - pointerStartRef.current.y;
    if (!didDragRef.current && Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
      didDragRef.current = true;
    }
    if (!didDragRef.current) return;

    const next = clampPos(
      clientX - dragOffsetRef.current.x,
      clientY - dragOffsetRef.current.y,
      target
    );
    positionRef.current = next;
    setButtonPosition(next);
  }, []);

  const endDrag = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    if (didDragRef.current) {
      persistPosition();
    }
  }, [persistPosition]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      moveDrag(e.clientX, e.clientY);
    };
    const onMouseUp = () => endDrag();

    const onTouchMove = (e) => {
      if (!isDraggingRef.current) return;
      if (e.cancelable) e.preventDefault();
      const t = e.touches[0];
      if (t) moveDrag(t.clientX, t.clientY);
    };
    const onTouchEnd = () => endDrag();

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("touchcancel", onTouchEnd);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [moveDrag, endDrag]);

  useEffect(() => {
    if (!buttonPosition) return undefined;
    const onResize = () => {
      const target = document.getElementById(FAB_ID);
      if (!target) return;
      const next = clampPos(buttonPosition.x, buttonPosition.y, target);
      if (next.x !== buttonPosition.x || next.y !== buttonPosition.y) {
        positionRef.current = next;
        setButtonPosition(next);
        try {
          localStorage.setItem(BUTTON_POS_KEY, JSON.stringify(next));
        } catch (_) {
          // ignore
        }
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [buttonPosition]);

  if (!token && !onOpen) return null;

  const style = buttonPosition
    ? { left: buttonPosition.x, top: buttonPosition.y }
    : undefined;

  const title =
    channelsCount > 1
      ? `Telefone Multiflow Voz — canal: ${channelName || "escolher"} (arraste para mover)`
      : "Abrir telefone (Multiflow Voz) — arraste para mover";

  const handleOpen = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    unlockVoiceAudio();
    maybeRequestVoiceNotifyOnGesture();
    try {
      if (typeof onOpen === "function") {
        onOpen();
        return;
      }
      toggleMultiflowVozSoftphone({ token });
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(
        err?.message ||
          "Configure a URL do Multiflow Voz em Configurações."
      );
    }
  };

  return (
    <div
      id={FAB_ID}
      className={`${classes.fab} ${
        !buttonPosition ? classes.fabPositionDefault : ""
      }`}
      style={style}
      title={title}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        beginDrag(e.clientX, e.clientY);
      }}
      onTouchStart={(e) => {
        const t = e.touches[0];
        if (!t) return;
        beginDrag(t.clientX, t.clientY);
      }}
    >
      <button
        type="button"
        className={classes.button}
        aria-label="Abrir telefone Multiflow Voz"
        onClick={handleOpen}
      >
        <span style={{ fontSize: 20 }} aria-hidden>
          📞
        </span>
      </button>
    </div>
  );
};

export default MultiflowVozFab;
